import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getCallsByPeriod } from "@/services/apiCallsService";
import { useToast } from "@/hooks/use-toast";
import { isAuthenticated } from "@/services/tokenService.ts";

interface ApiCallsChartProps {
  clientId?: string;
  workspaceId?: string;
}

export const ApiCallsChart = ({
  clientId,
}: ApiCallsChartProps = {}) => {
  const [timeFilter, setTimeFilter] = useState<"1h" | "24h" | "7d" | "30d">("24h");
  const [chartData, setChartData] = useState<Array<{ name: string; success: number; error: number }>>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (isAuthenticated()) {
      loadChartData();
    } else {
      setLoading(false);
      setChartData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, clientId]);

  const resolveClientIdParam = (): string | undefined => {
    if (clientId === "null") return "null";
    if (clientId && clientId !== "all") return clientId;
    return undefined;
  };

  const loadChartData = async () => {
    try {
      setLoading(true);

      const response = await getCallsByPeriod({
        period: timeFilter,
        client_id: resolveClientIdParam(),
      });

      if (!response.success || !response.data) {
        toast({
          title: "Erro",
          description: response.error?.message || "Erro ao carregar dados do gráfico",
          variant: "destructive",
        });
        setChartData([]);
        return;
      }

      setChartData(
        response.data.map((item) => ({
          name: item.name,
          success: item.success || 0,
          error: item.error || 0,
        })),
      );
    } catch {
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do gráfico",
        variant: "destructive",
      });
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  const getDataAndTitle = () => {
    switch (timeFilter) {
      case "1h":
        return {
          title: "Chamadas da API (1h)",
          description: "Volume de chamadas nas últimas 1 hora",
        };
      case "24h":
        return {
          title: "Chamadas da API (24h)",
          description: "Volume de chamadas nas últimas 24 horas",
        };
      case "7d":
        return {
          title: "Chamadas da API (7 dias)",
          description: "Volume de chamadas nos últimos 7 dias",
        };
      case "30d":
        return {
          title: "Chamadas da API (30 dias)",
          description: "Volume de chamadas nos últimos 30 dias",
        };
      default:
        return {
          title: "Chamadas da API (24h)",
          description: "Volume de chamadas nas últimas 24 horas",
        };
    }
  };

  const { title, description } = getDataAndTitle();
  const currentData = loading ? [] : chartData;

  return (
    <Card className="col-span-1">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <Select value={timeFilter} onValueChange={(value) => setTimeFilter(value as "1h" | "24h" | "7d" | "30d")}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">1 hora</SelectItem>
              <SelectItem value="24h">24 horas</SelectItem>
              <SelectItem value="7d">7 dias</SelectItem>
              <SelectItem value="30d">30 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-sm text-muted-foreground">Carregando dados...</div>
          </div>
        ) : currentData.length === 0 ? (
          <div className="flex items-center justify-center h-[300px]">
            <div className="text-sm text-muted-foreground">Nenhum dado disponível para o período selecionado</div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={currentData}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value, name) => [
                value,
                name === "success" ? "Sucesso" : "Erro",
              ]}
            />
            <Legend
              formatter={(value) => (value === "success" ? "Sucesso" : "Erro")}
              iconType="line"
              wrapperStyle={{
                paddingTop: "20px",
              }}
            />
            <Line
              type="monotone"
              dataKey="success"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              dot={{ fill: "hsl(var(--primary))", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: "hsl(var(--primary-glow))" }}
              name="success"
            />
            <Line
              type="monotone"
              dataKey="error"
              stroke="hsl(var(--destructive))"
              strokeWidth={3}
              dot={{ fill: "hsl(var(--destructive))", strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: "hsl(var(--destructive))" }}
              name="error"
            />
          </LineChart>
        </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};
